import { toEntries } from "@/helpers/utils.helper.ts";
import {
  ReportingBarTableCell,
  ReportingBarTableCellItem,
  ReportingBarTableCellProps,
} from "@/pages/reporting/components/ReportingBarTableCell.tsx";
import {
  Box,
  LinkButtonTo,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Button,
  Stack,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface ReportingBarTableRow {
  title: string;
  cells: ReportingBarTableCell[];
  entity_id?: number | null;
}

export interface Props {
  rows: ReportingBarTableRow[];
  onClick?: (row: ReportingBarTableRow, cell: ReportingBarTableCell) => void;
  className?: string;
  showMoreAfter?: number;
  titlePosition?: "side" | "top";
}

interface VMReportRow {
  title: string;
  row: ReportingBarTableRow;
  cells: VMReportCell[];
}

interface VMReportCellItem {
  width: number;
  title: string;
  value: number;
  bgColor?: string;
  bgHoverColor?: string;
}

interface VMReportCell extends ReportingBarTableCellProps {
  cell: ReportingBarTableCell;
  subCells?: VMReportCellItem[];
  tooltipContent?: string | React.ReactNode;
}

export const ReportingBarTable = ({
  rows,
  onClick,
  className,
  showMoreAfter,
  titlePosition = "side",
}: Props) => {
  const { t } = useTranslation();

  const calculatedReportRows = useMemo(() => {
    if (!rows) return [];

    const innerRows: VMReportRow[] = [];

    const totals: number[] = [];
    rows.forEach((row) => {
      row.cells.forEach((cell) => {
        if (cell.subCells) {
          cell.subCells.forEach((subCell) => {
            totals.push(subCell.value);
          });
        } else {
          totals.push(cell.value);
        }
      });
    });

    const maxDays = Math.max(...totals);
    const widthPerDay = 400 / Math.max(maxDays, 1);

    for (const reason of rows) {
      const cells: VMReportCell[] = [];
      for (const [cellIndex, cell] of toEntries(reason.cells)) {
        const vmCell: VMReportCell = {
          variant: cellIndex == 0 ? "primary" : "secondary",
          title: cell.title ?? cell.value.toString(),
          width: cell.value > 0 ? Math.round(Math.round(cell.value) * widthPerDay) + 10 : 2,
          cell: cell,
          bgColor: cell.bgColor,
          bgHoverColor: cell.bgHoverColor,
          tooltipContent: cell.tooltipContent,
        };

        if (cell.subCells && cell.subCells.length > 0) {
          vmCell.subCells = cell.subCells.map((subCell: ReportingBarTableCellItem) => ({
            width: subCell.value > 0 ? Math.round(Math.round(subCell.value) * widthPerDay) + 10 : 2,
            title: subCell.title ?? subCell.value.toString(),
            value: subCell.value,
            bgColor: subCell.bgColor,
            bgHoverColor: subCell.bgHoverColor,
          }));
        }

        cells.push(vmCell);
      }
      innerRows.push({
        title: reason.title,
        row: reason,
        cells: cells,
      });
    }

    return innerRows;
  }, [rows]);

  const [isShowAll, setIsShowAll] = useState(false);
  const itemsToShow = showMoreAfter && !isShowAll ? showMoreAfter : calculatedReportRows.length;

  return (
    <>
      <Box
        className={twMerge(
          titlePosition === "side"
            ? "grid md:grid-cols-12 gap-lg py-lg"
            : "flex flex-col gap-lg py-lg",
          className
        )}
      >
        {calculatedReportRows.slice(0, itemsToShow).map((item, index) => (
          <Stack
            className={titlePosition === "side" ? "md:col-span-12 grid md:grid-cols-12" : ""}
            key={`report-${index}`}
            gap={"xs"}
            direction={titlePosition === "side" ? "horizontal" : "vertical"}
          >
            <Box
              className={twMerge(titlePosition === "side" ? "md:col-span-3" : "font-semibold pb-1")}
            >
              {item.title}
            </Box>
            <Box className={twMerge(titlePosition === "side" && "md:col-span-9")}>
              <Stack gap={"xs"}>
                {item.cells.map((cell, cellIndex) => {
                  const hasSubCells = cell.subCells && cell.subCells.length > 0;

                  return (
                    <Stack key={`cell-${cellIndex}`} direction="horizontal" gap="sm">
                      {hasSubCells ? (
                        <>
                          {cell.tooltipContent ? (
                            <Tooltip>
                              <TooltipTrigger>
                                {onClick ? (
                                  <LinkButtonTo
                                    variant="plain"
                                    onClick={() => onClick(item.row, cell.cell)}
                                  >
                                    <Stack
                                      direction="horizontal"
                                      className="group overflow-hidden rounded-r-xs"
                                    >
                                      {cell.subCells?.map((subCell, subIndex) => (
                                        <ReportingBarTableCell
                                          key={`subcell-${subIndex}`}
                                          variant={cellIndex == 0 ? "primary" : "secondary"}
                                          title={subCell.title}
                                          width={subCell.width}
                                          bgColor={subCell.bgColor}
                                          bgHoverColor={subCell.bgHoverColor}
                                          isLast={subIndex === cell.subCells!.length - 1}
                                          hideTitle={true}
                                          useGroupHover={true}
                                        />
                                      ))}
                                    </Stack>
                                  </LinkButtonTo>
                                ) : (
                                  <Stack
                                    direction="horizontal"
                                    className="group overflow-hidden rounded-r-xs"
                                  >
                                    {cell.subCells?.map((subCell, subIndex) => (
                                      <ReportingBarTableCell
                                        key={`subcell-${subIndex}`}
                                        variant={cellIndex == 0 ? "primary" : "secondary"}
                                        title={subCell.title}
                                        width={subCell.width}
                                        bgColor={subCell.bgColor}
                                        bgHoverColor={subCell.bgHoverColor}
                                        isLast={subIndex === cell.subCells!.length - 1}
                                        hideTitle={true}
                                        useGroupHover={true}
                                      />
                                    ))}
                                  </Stack>
                                )}
                              </TooltipTrigger>
                              <TooltipContent>{cell.tooltipContent}</TooltipContent>
                            </Tooltip>
                          ) : onClick ? (
                            <LinkButtonTo
                              variant="plain"
                              onClick={() => onClick(item.row, cell.cell)}
                            >
                              <Stack
                                direction="horizontal"
                                className="group overflow-hidden rounded-r-xs"
                              >
                                {cell.subCells?.map((subCell, subIndex) => (
                                  <ReportingBarTableCell
                                    key={`subcell-${subIndex}`}
                                    variant={cellIndex == 0 ? "primary" : "secondary"}
                                    title={subCell.title}
                                    width={subCell.width}
                                    bgColor={subCell.bgColor}
                                    bgHoverColor={subCell.bgHoverColor}
                                    isLast={subIndex === cell.subCells!.length - 1}
                                    hideTitle={true}
                                    useGroupHover={true}
                                  />
                                ))}
                              </Stack>
                            </LinkButtonTo>
                          ) : (
                            <Stack
                              direction="horizontal"
                              className="group overflow-hidden rounded-r-xs"
                            >
                              {cell.subCells?.map((subCell, subIndex) => (
                                <ReportingBarTableCell
                                  key={`subcell-${subIndex}`}
                                  variant={cellIndex == 0 ? "primary" : "secondary"}
                                  title={subCell.title}
                                  width={subCell.width}
                                  bgColor={subCell.bgColor}
                                  bgHoverColor={subCell.bgHoverColor}
                                  isLast={subIndex === cell.subCells!.length - 1}
                                  hideTitle={true}
                                  useGroupHover={true}
                                />
                              ))}
                            </Stack>
                          )}
                          <span className={cellIndex == 0 ? "" : "text-secondary"}>
                            {cell.title}
                          </span>
                        </>
                      ) : (
                        <ReportingBarTableCell
                          onClick={
                            onClick
                              ? () => {
                                  onClick(item.row, cell.cell);
                                }
                              : undefined
                          }
                          variant={cell.variant}
                          title={cell.title}
                          width={cell.width}
                          bgColor={cell.bgColor}
                          bgHoverColor={cell.bgHoverColor}
                        />
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        ))}
      </Box>
      {itemsToShow < calculatedReportRows.length && (
        <Box className={"pt-(--gap-lg)"}>
          <Button variant={"secondary"} onClick={() => setIsShowAll(true)}>
            {t("common:show_more_template", { count: calculatedReportRows.length - itemsToShow })}
          </Button>
        </Box>
      )}
    </>
  );
};
